import { FormEvent, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import type { MenteePastSessionRow } from './MenteePortalService';

type Props = {
    show: boolean;
    session: MenteePastSessionRow | null;
    submitting: boolean;
    errorMessage: string | null;
    onClose: () => void;
    onSubmit: (stars: number, feedbackText: string | null) => void;
};

const MenteeSessionsRateModal = ({
    show,
    session,
    submitting,
    errorMessage,
    onClose,
    onSubmit,
}: Props) => {
    const [stars, setStars] = useState(0);
    const [feedback, setFeedback] = useState('');

    const handleSubmit = (event: FormEvent) => {
        event.preventDefault();
        if (stars < 1 || stars > 5) return;
        const trimmed = feedback.trim();
        onSubmit(stars, trimmed === '' ? null : trimmed);
    };

    const resetOnExited = () => {
        setStars(0);
        setFeedback('');
    };

    return (
        <Modal show={show} onHide={onClose} onExited={resetOnExited} centered>
            <Modal.Header closeButton>
                <Modal.Title>Rate session</Modal.Title>
            </Modal.Header>
            <form onSubmit={handleSubmit}>
                <Modal.Body>
                    {session ? (
                        <p className="msess-sub">
                            {session.occurredAt || 'Past session'}
                            {session.title ? ` · ${session.title}` : ''}
                        </p>
                    ) : null}
                    <div className="msess-field">
                        <span className="msess-field-label">Stars (required)</span>
                        <div className="msess-rate-pick" role="radiogroup" aria-label="Session rating">
                            {[1, 2, 3, 4, 5].map((value) => (
                                <button
                                    key={value}
                                    type="button"
                                    role="radio"
                                    aria-checked={stars === value}
                                    className={`msess-rate-star${stars >= value ? ' is-active' : ''}`}
                                    onClick={() => setStars(value)}
                                >
                                    ★
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="msess-field">
                        <label htmlFor="msess-feedback">Feedback (optional)</label>
                        <textarea
                            id="msess-feedback"
                            value={feedback}
                            onChange={(event) => setFeedback(event.target.value)}
                            placeholder="Share how the session went"
                            rows={3}
                        />
                    </div>
                    {errorMessage ? <p className="msess-error">{errorMessage}</p> : null}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-secondary" onClick={onClose} disabled={submitting}>
                        Cancel
                    </Button>
                    <Button type="submit" variant="primary" disabled={submitting || stars < 1}>
                        {submitting ? 'Submitting…' : 'Submit rating'}
                    </Button>
                </Modal.Footer>
            </form>
        </Modal>
    );
};

export default MenteeSessionsRateModal;
